import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import { NormalizedAppointment } from '../../hooks/useStatistics';

interface Props {
  appointments: NormalizedAppointment[];
  loading: boolean;
}

interface FunnelMetrics {
  obtained: number;
  confirmed: number;
  completed: number;
  cancelled: number;
  postponed: number;
  noReply: number;
  confirmationRate: number;
  completionRate: number;
}

const calculateFunnelMetrics = (appointments: NormalizedAppointment[]): FunnelMetrics => {
  const obtained = appointments.length;
  const confirmed = appointments.filter((a) =>
    a.status_normalized === 'confirme' || a.status_normalized === 'effectue'
  ).length;
  const completed = appointments.filter((a) => a.status_normalized === 'effectue').length;
  const cancelled = appointments.filter((a) => a.status_normalized === 'annule').length;
  const postponed = appointments.filter((a) => a.status_normalized === 'reporte').length;
  const noReply = appointments.filter((a) => a.status_normalized === 'no_reply').length;

  const confirmationRate = obtained > 0 ? (confirmed / obtained) * 100 : 0;
  const completionRate = confirmed > 0 ? (completed / confirmed) * 100 : 0;

  return { obtained, confirmed, completed, cancelled, postponed, noReply, confirmationRate, completionRate };
};

const StatisticsAppointmentFunnel: React.FC<Props> = ({ appointments, loading }) => {
  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.4 }}
        className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
      >
        <div className="h-5 bg-gray-200 rounded w-48 mb-6 animate-pulse" />
        <div className="flex items-center gap-4">
          <div className="h-20 flex-1 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-20 flex-1 bg-gray-100 rounded-lg animate-pulse" />
          <div className="h-20 flex-1 bg-gray-100 rounded-lg animate-pulse" />
        </div>
      </motion.div>
    );
  }

  const metrics = calculateFunnelMetrics(appointments);

  const steps = [
    {
      count: metrics.obtained,
      label: 'RDV obtenus',
      color: '#F43F5E',
      bg: 'rgba(244, 63, 94, 0.08)',
      borderColor: 'rgba(244, 63, 94, 0.2)',
    },
    {
      count: metrics.confirmed,
      label: 'Confirmes',
      color: '#3B82F6',
      bg: 'rgba(59, 130, 246, 0.08)',
      borderColor: 'rgba(59, 130, 246, 0.2)',
      rate: metrics.confirmationRate,
    },
    {
      count: metrics.completed,
      label: 'Effectues',
      color: '#10B981',
      bg: 'rgba(16, 185, 129, 0.08)',
      borderColor: 'rgba(16, 185, 129, 0.2)',
      rate: metrics.completionRate,
    },
  ];

  const secondaryItems: string[] = [];
  if (metrics.cancelled > 0) secondaryItems.push(`${metrics.cancelled} annule${metrics.cancelled > 1 ? 's' : ''}`);
  if (metrics.postponed > 0) secondaryItems.push(`${metrics.postponed} reporte${metrics.postponed > 1 ? 's' : ''}`);
  if (metrics.noReply > 0) secondaryItems.push(`${metrics.noReply} sans reponse`);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.4 }}
      className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-6">Parcours de vos rendez-vous</h3>

      <div className="flex items-stretch gap-2">
        {steps.map((step, i) => (
          <React.Fragment key={step.label}>
            {i > 0 && (
              <div className="flex flex-col items-center justify-center shrink-0 px-1">
                <span className="text-[11px] font-medium text-gray-400 whitespace-nowrap mb-0.5">
                  {step.rate?.toFixed(0)}%
                </span>
                <ChevronRight className="h-4 w-4 text-gray-300" />
              </div>
            )}
            <div
              className="flex-1 rounded-xl p-4 text-center transition-all hover:shadow-md flex flex-col justify-center min-w-0"
              style={{
                backgroundColor: step.bg,
                border: `1px solid ${step.borderColor}`,
              }}
            >
              <div className="text-2xl font-bold" style={{ color: step.color }}>
                {step.count}
              </div>
              <div className="text-xs font-medium text-gray-600 mt-1 truncate">{step.label}</div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {secondaryItems.length > 0 && (
        <div className="mt-5 text-center text-xs text-gray-400">
          {secondaryItems.join(' · ')}
        </div>
      )}
    </motion.div>
  );
};

export default StatisticsAppointmentFunnel;
